import React, { useState } from 'react';
import { Mail, Phone, Clock, Send, CheckCircle, MessageSquare } from 'lucide-react';
import { contactApi } from '../services/api';

interface FormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

const contactMethods = [
  {
    icon: <Mail size={20} />,
    title: 'Email',
    value: 'Info@kenluk.com',
    description: 'We respond within 24 hours',
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    border: 'border-blue-100',
    href: 'mailto:Info@kenluk.com',
  },
  {
    icon: <Phone size={20} />,
    title: 'Phone',
    value: '+234 708 832 9998',
    description: 'Mon–Fri, 9am–6pm WAT',
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    border: 'border-emerald-100',
    href: 'tel:+2347088329998',
  },
  {
    icon: <Clock size={20} />,
    title: 'Business Hours',
    value: '24/7 Support',
    description: 'Always available for urgent issues',
    color: 'text-purple-600',
    bg: 'bg-purple-50',
    border: 'border-purple-100',
    href: null,
  },
];

const subjectOptions = [
  'General Inquiry',
  'Account Support',
  'Payment Issue',
  'Technical Problem',
  'Partnership',
  'Other',
];

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    try {
      await contactApi.submitMessage(formData);
      setSubmitted(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error: any) {
      setSubmitError(error?.response?.data?.message || 'Unable to send your message. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <div className="bg-gradient-to-br from-fintech-primary/85 via-blue-800/78 to-slate-900/72 py-16 border-y border-white/20 backdrop-blur-[2px]">
        <div className="section-container text-center">
          <div className="public-readable-panel public-readable-panel--dark">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 border border-white/20 text-blue-100 text-xs font-semibold uppercase tracking-wider mb-5">
            Contact Us
          </div>
          <h1 className="text-4xl font-bold text-white mb-4 tracking-tight">Get in Touch</h1>
          <p className="text-blue-200 text-lg max-w-xl mx-auto">
            Have questions about Kenluk Pay? Our team is ready to help you get started.
          </p>
          </div>
        </div>
      </div>

      <div className="section-container py-14">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
          {/* Contact Info */}
          <div className="lg:col-span-2">
            <div className="card p-5 mb-5">
              <h2 className="text-xl font-bold text-fintech-text mb-2">Contact Information</h2>
              <p className="text-fintech-textMuted text-sm mb-0 leading-relaxed">
                Reach out through any of these channels and we'll get back to you as quickly as possible.
              </p>
            </div>

            <div className="flex flex-col gap-3">
              {contactMethods.map((method, i) => (
                <div key={i} className="card p-5 flex items-start gap-4 hover:shadow-md transition-shadow">
                  <div className={`w-10 h-10 rounded-xl ${method.bg} border ${method.border} ${method.color} flex items-center justify-center flex-shrink-0`}>
                    {method.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-fintech-textMuted uppercase tracking-wider mb-0.5">{method.title}</p>
                    {method.href ? (
                      <a href={method.href} className={`font-semibold text-sm ${method.color} no-underline hover:underline`}>
                        {method.value}
                      </a>
                    ) : (
                      <p className="font-semibold text-fintech-text text-sm">{method.value}</p>
                    )}
                    <p className="text-xs text-fintech-textMuted mt-0.5">{method.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* FAQ hint */}
            <div className="mt-6 p-4 rounded-xl bg-white/85 border border-blue-200/80 backdrop-blur-sm">
              <div className="flex items-start gap-3">
                <MessageSquare size={16} className="text-fintech-secondary flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-fintech-text mb-1">Looking for quick answers?</p>
                  <p className="text-xs text-fintech-textMuted">
                    Check our FAQ section for answers to the most common questions about our platform.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-3">
            <div className="card p-8">
              {submitted ? (
                <div className="text-center py-10">
                  <div className="w-16 h-16 rounded-full bg-emerald-50 border-2 border-emerald-200 flex items-center justify-center mx-auto mb-5">
                    <CheckCircle size={32} className="text-fintech-accent" />
                  </div>
                  <h3 className="text-xl font-bold text-fintech-text mb-2">Message Sent!</h3>
                  <p className="text-fintech-textMuted mb-6 max-w-xs mx-auto">
                    Thank you for reaching out. We'll get back to you within 24 hours.
                  </p>
                  <button
                    className="btn-secondary"
                    onClick={() => setSubmitted(false)}
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <>
                  <div className="mb-6">
                    <h2 className="text-xl font-bold text-fintech-text mb-1">Send a Message</h2>
                    <p className="text-sm text-fintech-textMuted">Fill out the form below and we'll respond promptly.</p>
                  </div>
                  {submitError && (
                    <div className="mb-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                      {submitError}
                    </div>
                  )}
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div className="form-group mb-0">
                        <label className="form-label" htmlFor="contact-name">Full Name *</label>
                        <input
                          type="text"
                          id="contact-name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          className="form-input"
                          placeholder="John Doe"
                          required
                        />
                      </div>
                      <div className="form-group mb-0">
                        <label className="form-label" htmlFor="contact-email">Email Address *</label>
                        <input
                          type="email"
                          id="contact-email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className="form-input"
                          placeholder="john@company.com"
                          required
                        />
                      </div>
                    </div>

                    <div className="form-group mb-0">
                      <label className="form-label" htmlFor="contact-subject">Subject *</label>
                      <select
                        id="contact-subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        className="form-select"
                        required
                      >
                        <option value="">Select a subject...</option>
                        {subjectOptions.map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group mb-0">
                      <label className="form-label" htmlFor="contact-message">Message *</label>
                      <textarea
                        id="contact-message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        className="form-input resize-none"
                        placeholder="Tell us more about your inquiry..."
                        required
                        rows={5}
                      />
                    </div>

                    <button
                      type="submit"
                      className="btn-primary w-full justify-center"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner w-4 h-4" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send size={16} />
                          Send Message
                        </>
                      )}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
